import { canvas } from '@bb/contracts';
import { ApiError, type CanvasSimulationBody, type CanvasSimulationEvent, type CanvasSimulationResult } from '../../../api/client.ts';

/**
 * The Test Canvas form (BB-122) and what it sends: who runs through the Canvas (a Content test member or a made-up
 * profile), which version and variant, how they enter, a script of events at relative (`+5m`, `+3d`) or absolute
 * times, and how far the virtual clock may run. Rules come from the contract (`SimulationBody`, `SimulationTime`).
 */
export type EventType = CanvasSimulationEvent['type'];

export const EVENT_TYPES: Array<{ value: EventType; label: string }> = [
  { value: 'custom_event', label: 'Custom event' },
  { value: 'attribute_change', label: 'Attribute change' },
  { value: 'session_start', label: 'Session start' },
  { value: 'subscription_change', label: 'Subscription change' },
  { value: 'message_interaction', label: 'Message interaction' },
];

export interface EventRow {
  type: EventType;
  at: string;
  name: string;
  properties: string;
  attribute: string;
  value: string;
  appInstanceId: string;
  groupId: string;
  state: 'subscribed' | 'unsubscribed';
  messageApiId: string;
  interaction: 'click' | 'open' | 'impression';
}

export const newEventRow = (type: EventType = 'custom_event', at = '+5m'): EventRow => ({ type, at, name: '', properties: '', attribute: '', value: '', appInstanceId: '', groupId: '', state: 'subscribed', messageApiId: '', interaction: 'click' });

export interface SyntheticFields {
  externalId: string;
  firstName: string;
  email: string;
  language: string;
  country: string;
  timezone: string;
  customAttributes: string;
  randomBucket: string;
}

export interface SimForm {
  source: 'draft' | 'published';
  profileKind: 'test' | 'synthetic';
  testProfileId: string;
  synthetic: SyntheticFields;
  variantId: string;
  untilDuration: string;
  context: string;
  entryEventName: string;
  entryEventProperties: string;
  events: EventRow[];
}

export const emptySimForm = (): SimForm => ({
  source: 'draft',
  profileKind: 'synthetic',
  testProfileId: '',
  synthetic: { externalId: 'sim-user-1', firstName: '', email: '', language: '', country: '', timezone: '', customAttributes: '', randomBucket: '' },
  variantId: '',
  untilDuration: '',
  context: '',
  entryEventName: '',
  entryEventProperties: '',
  events: [],
});

/** A JSON object typed by the editor: empty — `{}`; anything but an object — an error. */
function jsonObject(text: string): { value: Record<string, unknown>; error?: string } {
  if (!text.trim()) return { value: {} };
  try {
    const value: unknown = JSON.parse(text);
    if (value && typeof value === 'object' && !Array.isArray(value)) return { value: value as Record<string, unknown> };
  } catch {
    // Reported below.
  }
  return { value: {}, error: 'Enter a JSON object, e.g. {"symbol": "EURUSD"}.' };
}

/** An attribute value: JSON when it parses (`null`, `3`, `"pt"`), else the text as typed. */
const attributeValue = (text: string): unknown => {
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return text;
  }
};

/** Why a scripted event cannot be sent, by field (`at`, `name`, `properties`, …). */
export function eventRowIssues(row: EventRow): Record<string, string> {
  const issues: Record<string, string> = {};
  if (!canvas.SimulationTime.safeParse(row.at.trim()).success) issues.at = 'Use +30s, +5m, +2h, +3d, +1w or an ISO time with an offset.';
  if (row.type === 'custom_event') {
    if (!row.name.trim()) issues.name = 'Enter the event name.';
    const props = jsonObject(row.properties);
    if (props.error) issues.properties = props.error;
  }
  if (row.type === 'attribute_change' && !row.attribute.trim()) issues.attribute = 'Enter the attribute (standard ones start with $, e.g. $language).';
  if (row.type === 'subscription_change' && !/^[0-9a-f-]{36}$/i.test(row.groupId.trim())) issues.groupId = 'Enter the subscription group ID (UUID).';
  if (row.type === 'message_interaction' && !/^[0-9a-f-]{36}$/i.test(row.messageApiId.trim())) issues.messageApiId = 'Enter the message API ID (UUID).';
  if (row.type === 'session_start' && row.appInstanceId.trim() && !/^[0-9a-f-]{36}$/i.test(row.appInstanceId.trim())) issues.appInstanceId = 'An app instance ID is a UUID; leave empty for any app.';
  return issues;
}

function eventOf(row: EventRow): CanvasSimulationEvent {
  const at = row.at.trim();
  switch (row.type) {
    case 'custom_event':
      return { type: 'custom_event', at, name: row.name.trim(), properties: jsonObject(row.properties).value };
    case 'attribute_change':
      return { type: 'attribute_change', at, attribute: row.attribute.trim(), value: attributeValue(row.value) };
    case 'session_start':
      return { type: 'session_start', at, appInstanceId: row.appInstanceId.trim() || null };
    case 'subscription_change':
      return { type: 'subscription_change', at, groupId: row.groupId.trim(), state: row.state };
    case 'message_interaction':
      return { type: 'message_interaction', at, messageApiId: row.messageApiId.trim(), interaction: row.interaction };
  }
}

export interface FormCheck {
  body: CanvasSimulationBody | null;
  /** Problems by field: `profile`, `synthetic.customAttributes`, `untilDuration`, `context`, `events.2.at`, … */
  issues: Record<string, string>;
}

/** The request of the form, or why it cannot be sent yet. */
export function buildSimulation(form: SimForm): FormCheck {
  const issues: Record<string, string> = {};
  let profile: CanvasSimulationBody['profile'] | null = null;
  if (form.profileKind === 'test') {
    if (form.testProfileId) profile = { testProfileId: form.testProfileId };
    else issues.profile = 'Choose a test user.';
  } else {
    const s = form.synthetic;
    const custom = jsonObject(s.customAttributes);
    if (custom.error) issues['synthetic.customAttributes'] = custom.error;
    const bucket = s.randomBucket.trim();
    if (bucket && !(/^\d+$/.test(bucket) && Number(bucket) <= 9999)) issues['synthetic.randomBucket'] = 'A whole number from 0 to 9999.';
    const text = (key: keyof SyntheticFields) => (s[key].trim() ? { [key]: s[key].trim() } : {});
    profile = { synthetic: { ...text('externalId'), ...text('firstName'), ...text('email'), ...text('language'), ...text('country'), ...text('timezone'), customAttributes: custom.value, ...(bucket ? { randomBucket: Number(bucket) } : {}) } };
  }
  const until = form.untilDuration.trim();
  if (until) {
    const ms = canvas.simulationDurationMs(until);
    if (ms === null || until.startsWith('+')) issues.untilDuration = 'Use a duration such as 45m, 12h, 31d or 8w.';
    else if (ms <= 0 || ms > canvas.SIMULATION_LIMITS.untilDays * 86_400_000) issues.untilDuration = `Between 1 second and ${canvas.SIMULATION_LIMITS.untilDays} days.`;
  }
  const context = jsonObject(form.context);
  if (context.error) issues.context = context.error;
  const entryProps = jsonObject(form.entryEventProperties);
  if (entryProps.error) issues.entryEventProperties = entryProps.error;
  if (form.events.length > canvas.SIMULATION_LIMITS.events) issues.events = `At most ${canvas.SIMULATION_LIMITS.events} events.`;
  form.events.forEach((row, i) => {
    for (const [field, message] of Object.entries(eventRowIssues(row))) issues[`events.${i}.${field}`] = message;
  });
  if (Object.keys(issues).length || !profile) return { body: null, issues };
  const body: CanvasSimulationBody = {
    definitionSource: form.source,
    profile,
    entry: {
      context: context.value,
      ...(form.entryEventName.trim() ? { event: { name: form.entryEventName.trim(), properties: entryProps.value } } : {}),
      ...(form.variantId ? { variantId: form.variantId } : {}),
    },
    events: form.events.map(eventOf),
    ...(until ? { untilDuration: until } : {}),
    deliveryMode: 'dry_run',
  };
  // The contract has the last word (lengths, formats) before anything is sent.
  const parsed = canvas.SimulationBody.safeParse(body);
  if (!parsed.success) return { body: null, issues: Object.fromEntries(parsed.error.issues.map((i) => (i.path[1] === 'testProfileId' ? ['profile', 'Not a valid test user ID.'] : [i.path.join('.') || 'form', i.message]))) };
  return { body, issues };
}

/** Fields the form shows next to their control; any other problem is listed with the run button. */
export const isShownIssue = (key: string) => /^(profile|synthetic\.(customAttributes|randomBucket)|untilDuration|context|entryEventProperties|events|events\.\d+\.(at|name|properties|attribute|groupId|messageApiId|appInstanceId))$/.test(key);

/** `+3 d 4 h`, `+5 min`, `+0 s`: an offset of the virtual clock. */
export function formatOffset(seconds: number): string {
  if (seconds <= 0) return '+0 s';
  const parts: string[] = [];
  let left = Math.round(seconds);
  for (const [size, unit] of [
    [86_400, 'd'],
    [3_600, 'h'],
    [60, 'min'],
    [1, 's'],
  ] as const) {
    const n = Math.floor(left / size);
    if (n) parts.push(`${n} ${unit}`);
    left -= n * size;
    if (parts.length === 2) break;
  }
  return `+${parts.join(' ')}`;
}

export const OUTCOME_TEXT: Record<CanvasSimulationResult['outcome']['status'], { label: string; tone: 'success' | 'info' | 'warning' | 'danger' | 'neutral' }> = {
  completed: { label: 'Completed', tone: 'success' },
  exited: { label: 'Exited', tone: 'info' },
  failed: { label: 'Failed', tone: 'danger' },
  waiting: { label: 'Still waiting', tone: 'warning' },
  rejected: { label: 'Did not enter', tone: 'warning' },
  limit_exceeded: { label: 'Stopped: step limit', tone: 'danger' },
  timeout: { label: 'Stopped: time limit', tone: 'danger' },
};

/** The run's problem in words; `null` for `canvas_invalid`, whose issues the panel lists itself. */
export function simulationErrorText(error: unknown): string {
  if (!(error instanceof ApiError)) return 'The simulation could not run.';
  const p = error.problem as { index?: number; issues?: Array<{ path?: string; message?: string }> };
  const event = typeof p.index === 'number' ? `Event ${p.index + 1}: ` : '';
  switch (error.code) {
    case 'test_send_unavailable':
      return 'Real test sends to a test user’s devices come with BB-59. Run a dry run: it renders every message and sends nothing.';
    case 'canvas_invalid':
      return 'The Canvas cannot run as it is:';
    case 'not_a_test_profile':
      return 'Only members of a Content test internal group can run with their real data: add the user to one in Settings → Internal groups, or use a synthetic user.';
    case 'not_published':
      return 'The Canvas has no published version yet: simulate the draft.';
    case 'simulation_busy':
      return 'Too many simulations are running right now: try again in a few seconds.';
    case 'simulation_conflict':
      return 'The test user is busy with a real send right now: try again in a moment.';
    case 'profile_not_found':
      return 'The test user no longer exists in this environment.';
    case 'canvas_not_found':
      return 'The Canvas was not found in this environment.';
    case 'unknown_variant':
      return 'The Canvas has no such variant any more: choose another one.';
    case 'invalid_settings':
      return `The Canvas entry settings are invalid${p.issues?.length ? `: ${p.issues.map((i) => `${i.path ?? ''} ${i.message ?? ''}`.trim()).join('; ')}` : ''}. Fix them in the Canvas settings and save.`;
    case 'event_before_start':
    case 'unknown_attribute':
    case 'invalid_attribute_value':
      return `${event}${error.message}.`;
    case 'validation_failed':
      return `Check the form: ${error.message}${p.issues?.length ? ` — ${p.issues.map((i) => `${i.path ?? ''}: ${i.message ?? ''}`).join('; ')}` : ''}`;
    default:
      if (error.status === 403) return 'Testing a Canvas needs the canvas.edit permission.';
      return `${error.message} (${error.code})`;
  }
}
